import { useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Alert } from '@mui/material';
import { useDispatch } from 'react-redux';
import api from '../../../../app/api/axios-api';
import { setToast } from '../../../../components/Toasts/data-access/toast-reducer';
import { canReviewerSubmitReview, isApprovedReviewStatus } from '../../shared/reviewSubmission.utils';

interface FinishSubmitTabProps {
  projectId: string;
  userId: string;
  reviewStatus?: string | null;
  project?: Record<string, unknown> | null;
  onReviewSubmitted?: () => Promise<void> | void;
}

interface ReviewResponse {
  status: number;
  message: string;
  review_id: string;
  projectId: string;
  domain: string;
  timestamp: string;
  environment: string;
}

const FinishSubmitTab = ({ projectId, userId, reviewStatus, project, onReviewSubmitted }: FinishSubmitTabProps) => {
  const dispatch = useDispatch();
  const [reviewerComments, setReviewerComments] = useState<string>('');
  const [submittingAction, setSubmittingAction] = useState<'approve' | 'reject' | null>(null);
  const canSubmitReview = canReviewerSubmitReview(reviewStatus, project);
  const showApprovedLockedState = isApprovedReviewStatus(reviewStatus) && !canSubmitReview;
  const showReReviewNotice = isApprovedReviewStatus(reviewStatus) && canSubmitReview;

  const handleApprove = async () => {
    if (!projectId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project ID is required. Please select a project.'
        })
      );
      return;
    }

    setSubmittingAction('approve');

    try {
      // Get reviewer information from localStorage
      const reviewerName = localStorage.getItem('firstName') || localStorage.getItem('username') || '';
      const reviewerRole = localStorage.getItem('role') || '';

      const requestData = {
        projectId: projectId,
        reviewer_comment: reviewerComments || '',
        userId: userId || undefined,
        reviewer_name: reviewerName || undefined,
        reviewer_role: reviewerRole || undefined,
        verdict: 'APPROVE'
      };

      const response = await api.post<ReviewResponse>('/project/features/review', requestData);

      if (response.data && response.data.status === 200) {
        await onReviewSubmitted?.();
        dispatch(
          setToast({
            show: true,
            title: 'Success',
            message: `Review submitted successfully. Review ID: ${response.data.review_id}`
          })
        );
        setReviewerComments('');
      } else {
        throw new Error(response.data?.message || 'Failed to submit review');
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit review. Please try again.';

      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: errorMessage
        })
      );
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleReject = async () => {
    if (!projectId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project ID is required. Please select a project.'
        })
      );
      return;
    }

    setSubmittingAction('reject');

    try {
      const reviewerName = localStorage.getItem('firstName') || localStorage.getItem('username') || '';
      const reviewerRole = localStorage.getItem('role') || '';

      const requestData = {
        projectId: projectId,
        reviewer_comment: reviewerComments || '',
        userId: userId || undefined,
        reviewer_name: reviewerName || undefined,
        reviewer_role: reviewerRole || undefined,
        verdict: 'REJECT'
      };

      const response = await api.post<ReviewResponse>('/project/features/review', requestData);

      if (response.data && response.data.status === 200) {
        await onReviewSubmitted?.();
        dispatch(
          setToast({
            show: true,
            title: 'Rejection Successful',
            message: `Review has been rejected successfully. Review ID: ${response.data.review_id}`
          })
        );
        setReviewerComments('');
      } else {
        throw new Error(response.data?.message || 'Failed to submit review');
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit review. Please try again.';

      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: errorMessage
        })
      );
    } finally {
      setSubmittingAction(null);
    }
  };

  const isSubmitting = submittingAction !== null;

  return (
    <Box sx={{ padding: '24px 32px' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 4 }}>
        Submit & Finish
      </Typography>

      {showApprovedLockedState ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.light',
            backgroundColor: '#f2faf4'
          }}
        >
          <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 600, mb: 1 }}>
            Thank you for your approval.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This review has already been approved. No further action is required unless the project is updated and
            resubmitted for review.
          </Typography>
        </Box>
      ) : (
        <>
          {showReReviewNotice && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This project was updated after your previous approval. Submit a new review when you have verified the
              latest changes.
            </Alert>
          )}
          <Box sx={{ mb: 4 }}>
            <TextField
              label="Reviewer Comments"
              placeholder="Enter your review comments here..."
              multiline
              rows={6}
              fullWidth
              value={reviewerComments}
              onChange={e => setReviewerComments(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleReject}
              disabled={isSubmitting}
              sx={{
                minWidth: 120,
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1.5
              }}
            >
              {submittingAction === 'reject' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Submitting...</span>
                </Box>
              ) : (
                'Reject'
              )}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApprove}
              disabled={isSubmitting}
              sx={{
                minWidth: 120,
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1.5
              }}
            >
              {submittingAction === 'approve' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Submitting...</span>
                </Box>
              ) : (
                'Approve'
              )}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FinishSubmitTab;
